import React from "react";
import { NavLink as RouterNavLink, Link, Outlet, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import "./nocturne.css";

const NAV_ITEMS = [
    { to: "/sifcontrol/rankings", label: "Rankings" },
    { to: "/sifcontrol/screener", label: "Screener" },
    { to: "/sifcontrol/compare", label: "Compare" },
    { to: "/sifcontrol/sifs", label: "SIFs" },
    { to: "/sifcontrol/amcs", label: "AMCs" },
];

const linkClass = ({ isActive }) =>
    `text-sm font-body transition-colors ${
        isActive ? "text-oth-ink font-medium" : "text-oth-ink3 hover:text-oth-ink"
    }`;

export default function SifControlLayout() {
    const { pathname } = useLocation();
    const isAmcPortal = pathname.toLowerCase().startsWith("/sifcontrol/amc");
    const isAdminPortal = pathname.toLowerCase().startsWith("/sifcontrol/admin");

    return (
        <div className="sc-nocturne min-h-screen bg-oth-bg flex flex-col" data-testid="sifcontrol-shell">
            <header
                className="sticky top-0 z-40 border-b"
                style={{
                    background: "rgba(11, 12, 15, 0.78)",
                    backdropFilter: "blur(18px)",
                    WebkitBackdropFilter: "blur(18px)",
                    borderColor: "rgba(255, 255, 255, 0.07)",
                }}
                data-testid="sifcontrol-header"
            >
                <div className="mx-auto max-w-[1240px] px-4 md:px-6 h-14 md:h-16 flex items-center gap-4 md:gap-8">
                    <Link
                        to="/"
                        className="hidden md:inline-flex items-center gap-1.5 text-xs font-body text-oth-ink3 hover:text-oth-ink transition-colors"
                        data-testid="sifcontrol-back-oth"
                    >
                        <ArrowLeft size={14} /> One Tree Hill
                    </Link>
                    <Link
                        to="/sifcontrol"
                        className="font-display text-lg md:text-xl font-semibold tracking-tight text-oth-ink"
                        data-testid="sifcontrol-brand"
                    >
                        SIF Control
                        <span className="ml-2 text-[10px] uppercase tracking-[0.18em] text-oth-ink3 font-body font-normal align-middle">
                            by One Tree Hill
                        </span>
                    </Link>
                    <nav className="hidden md:flex items-center gap-6 ml-auto" data-testid="sifcontrol-nav">
                        {NAV_ITEMS.map((n) => (
                            <RouterNavLink key={n.to} to={n.to} className={linkClass} data-testid={`sc-nav-${n.label.toLowerCase()}`}>
                                {n.label}
                            </RouterNavLink>
                        ))}
                        {!isAmcPortal && !isAdminPortal && (
                            <Link
                                to="/sifcontrol/amc/login"
                                className="btn-pill btn-pill-dark !py-2 !px-4 !text-xs"
                                data-testid="sc-login-btn"
                            >
                                Login
                            </Link>
                        )}
                        {isAmcPortal && (
                            <Link to="/sifcontrol" className="text-sm font-body text-oth-ink3 hover:text-oth-ink" data-testid="sc-exit-amc">
                                Exit portal
                            </Link>
                        )}
                    </nav>
                    <div className="md:hidden ml-auto">
                        <Link to="/" className="text-xs font-body text-oth-ink3" data-testid="sc-mobile-back">← OTH</Link>
                    </div>
                </div>
                <div className="md:hidden border-t border-oth-line/60">
                    <div className="mx-auto max-w-[1240px] px-3 py-2 flex items-center gap-4 overflow-x-auto thin-scroll">
                        {NAV_ITEMS.map((n) => (
                            <RouterNavLink key={n.to} to={n.to} className={linkClass} data-testid={`sc-mnav-${n.label.toLowerCase()}`}>
                                {n.label}
                            </RouterNavLink>
                        ))}
                    </div>
                </div>
            </header>

            <main className="flex-1" data-testid="sifcontrol-main">
                <Outlet />
            </main>

            <footer className="border-t border-oth-line/60 mt-16 py-10" data-testid="sifcontrol-footer">
                <div className="mx-auto max-w-[1240px] px-4 md:px-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                    <div>
                        <div className="font-display text-base font-semibold tracking-tight text-oth-ink">SIF Control</div>
                        <div className="text-xs font-body text-oth-ink3 mt-0.5">By One Tree Hill Prime Wealth</div>
                    </div>
                    <nav className="flex flex-wrap gap-x-6 gap-y-2 text-xs font-body text-oth-ink3">
                        <a href="https://onetreehill.in" className="hover:text-oth-ink transition-colors" data-testid="sc-footer-oth">One Tree Hill</a>
                        <Link to="/sifcontrol/disclaimer" className="hover:text-oth-ink transition-colors" data-testid="sc-footer-disclaimer">Disclaimer</Link>
                        <Link to="/sifcontrol/privacy" className="hover:text-oth-ink transition-colors" data-testid="sc-footer-privacy">Privacy Policy</Link>
                        <Link to="/sifcontrol/terms" className="hover:text-oth-ink transition-colors" data-testid="sc-footer-terms">Terms</Link>
                        <a href="mailto:reach@onetreehill.in" className="hover:text-oth-ink transition-colors" data-testid="sc-footer-contact">Contact</a>
                    </nav>
                </div>
                <div className="mx-auto max-w-[1240px] px-4 md:px-6 mt-8 text-[11px] font-body text-oth-ink3/80 leading-relaxed max-w-[820px]" data-testid="sc-footer-note">
                    SIF Control is an information and research tool provided by One Tree Hill Prime Wealth. Rankings and analytics are based on historical data
                    and selected methodologies and should not be construed as investment advice, recommendations or guarantees of future performance.
                </div>
            </footer>
        </div>
    );
}
